/** RecordingPlayer — minimal <audio> wrapper backed by a presigned URL.
 *
 *  Fetches the URL via useMeetingAudioUrl when `active` (deferred until the
 *  user actually opens the player, so a long list of recordings doesn't
 *  pre-warm N URLs). Shows a friendly loading / error state.
 */

import { useMeetingAudioUrl } from "../../api/queries";
import styles from "../../styles/recording.module.css";

interface RecordingPlayerProps {
  meetingId: string;
  /** When false, the URL fetch is skipped. Default true. */
  active?: boolean;
}

function formatSize(bytes: number | null | undefined): string {
  if (!bytes) return "";
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / 1024 / 1024).toFixed(1)} MB`;
}

function formatDuration(seconds: number | null | undefined): string {
  if (!seconds || seconds <= 0) return "";
  const m = Math.floor(seconds / 60);
  const s = Math.floor(seconds % 60);
  return `${String(m).padStart(2, "0")}:${String(s).padStart(2, "0")}`;
}

export default function RecordingPlayer({
  meetingId,
  active = true,
}: RecordingPlayerProps) {
  const { data, isLoading, error } = useMeetingAudioUrl(meetingId, active);

  if (!active) return null;

  if (isLoading) {
    return <div className={styles.playerLoading}>正在准备音频…</div>;
  }

  if (error || !data?.url) {
    return (
      <div className={styles.playerError}>
        {(error as Error)?.message ?? "音频加载失败"}
      </div>
    );
  }

  return (
    <div className={styles.playerBox}>
      <audio controls preload="metadata" src={data.url} className={styles.playerAudio}>
        您的浏览器不支持音频播放。
      </audio>
      <div className={styles.playerMeta}>
        {data.filename && <span className={styles.playerFilename}>{data.filename}</span>}
        {data.durationSeconds ? (
          <span className={styles.playerDuration}>
            时长 {formatDuration(data.durationSeconds)}
          </span>
        ) : null}
        {data.sizeBytes ? (
          <span className={styles.playerSize}>{formatSize(data.sizeBytes)}</span>
        ) : null}
      </div>
    </div>
  );
}
